import { captureSupportScope, internalApiHeaders } from '../admin/supportTransport';
import { companiesApi, executionApi, questionsApi, tasksApi } from '../services/api';
import {
  normalizeReportSource,
  type ReportCompany,
  type ReportDocumentV2,
  type ReportSourceSnapshot,
  type ReportSectionAnalysis,
} from './model';

export interface ReportConfiguration {
  id: string;
  companyId: string;
  updatedAt: string;
  document?: ReportDocumentV2 | null;
  [key: string]: unknown;
}

export class ReportApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
  }
}

const base = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

export async function reportRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const requestScope = captureSupportScope();
  const token = await (window as any).Clerk?.session?.getToken();
  if (!token) throw new ReportApiError('Your session has ended. Sign in to continue.', 401);
  const response = await fetch(`${base}${path}`, {
    ...options,
    headers: internalApiHeaders(
      `${base}${path}`,
      {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
      false,
      requestScope
    ),
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.success) {
    throw new ReportApiError(
      result?.error || 'The report service is unavailable. Your draft is still here.',
      response.status
    );
  }
  return result.data;
}

export function loadReportConfiguration(companyId: string, reportId?: string) {
  return reportRequest<ReportConfiguration | null>(
    `/visual-reports/company/${encodeURIComponent(companyId)}/config${reportId ? `/${encodeURIComponent(reportId)}` : ''}`
  );
}

export async function saveReportConfiguration(
  companyId: string,
  document: ReportDocumentV2,
  identity: { id: string; updatedAt?: string; exists: boolean }
): Promise<ReportConfiguration> {
  const creating = !identity.exists;
  const name = document.metadata.title.trim().slice(0, 255);
  const configuration = await reportRequest<ReportConfiguration>(
    `/visual-reports/company/${companyId}/config${identity.exists ? `/${identity.id}` : ''}`,
    {
      method: identity.exists ? 'PUT' : 'POST',
      body: JSON.stringify({
        id: identity.id,
        // An empty authored title is valid document content. Keep the existing
        // configuration name on update, and give new records a usable fallback.
        ...(name ? { name } : creating ? { name: 'Strategic Report' } : {}),
        document,
        ...(identity.exists
          ? { expectedUpdatedAt: identity.updatedAt }
          : { isDefault: true, expectNoConfiguration: true }),
      }),
    }
  );
  // A lost create response may be retried after more edits. The server returns
  // the original record for this UUID; keep its version before sending new text.
  Object.assign(identity, {
    id: configuration.id,
    updatedAt: configuration.updatedAt,
    exists: true,
  });
  if (creating && canonicalJson(configuration.document) !== canonicalJson(document)) {
    return saveReportConfiguration(companyId, document, identity);
  }
  if (canonicalJson(configuration.document) !== canonicalJson(document)) {
    throw new ReportApiError(
      'The saved report did not match this draft. Your edits are retained; retry saving.',
      502
    );
  }
  return configuration;
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object')
    return `{${Object.entries(value)
      .filter(([, item]) => item !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`)
      .join(',')}}`;
  return JSON.stringify(value) ?? 'undefined';
}

export async function loadReportSource(company: ReportCompany): Promise<ReportSourceSnapshot> {
  const errors: string[] = [];
  const read = async (label: string, request: Promise<any>) => {
    try {
      const result = await request;
      if (!result.success) throw new Error(result.error);
      return result.data;
    } catch {
      errors.push(`${label} could not be refreshed`);
      return undefined;
    }
  };
  const categories = [
    ['swot', 'strengths', 'swot.strengths_and_competitive_assets'],
    ['swot', 'weaknesses', 'swot.weaknesses_and_competitive_deficiencies'],
    ['swot', 'opportunities', 'swot.market_opportunities'],
    ['swot', 'threats', 'swot.external_threats'],
    ['successFactors', 'productAttributes', 'industry_success_factors.product_attributes'],
    [
      'successFactors',
      'serviceCharacteristics',
      'industry_success_factors.service_characteristics',
    ],
    ['successFactors', 'resources', 'industry_success_factors.resources'],
    [
      'successFactors',
      'competitiveCapabilities',
      'industry_success_factors.competitive_capabilities',
    ],
    ['successFactors', 'shortcomings', 'industry_success_factors.shortcomings'],
  ];
  const [summary, foundation, execution, tasks, findings] = await Promise.all([
    read('Assessment summary', companiesApi.getSummaryAnalysis(company.id)),
    read('Strategic foundation', companiesApi.loadFoundation(company.id)),
    read('Execution progress', executionApi.getExecutionHierarchy(company.id)),
    read('Task details', tasksApi.list({ company: company.id })),
    Promise.all(
      categories.map(async ([group, key, entity]) => {
        const questions = await read(
          `${key} findings`,
          questionsApi.getEntityQuestions(entity, company.id)
        );
        const items = questions
          ?.filter((q: any) => q.responses?.[0]?.response?.booleanValue === true)
          .map((q: any) => ({
            id: q.id,
            content: q.content,
            impact:
              ({ low: 1, medium: 2, high: 3 } as Record<string, number>)[
                q.responses[0].response.impactLevel
              ] ?? null,
          }));
        return { group, key, items };
      })
    ),
  ]);
  const swotWithImpact: Record<string, unknown> = {};
  const successFactorsWithImpact: Record<string, unknown> = {};
  for (const entry of findings) {
    if (entry.items !== undefined)
      (entry.group === 'swot' ? swotWithImpact : successFactorsWithImpact)[entry.key] = entry.items;
  }
  return normalizeReportSource(company, {
    summary,
    foundation,
    execution:
      execution || tasks ? { ...execution, ...(tasks ? { tasks: tasks.all } : {}) } : undefined,
    swotWithImpact,
    successFactorsWithImpact,
    loadErrors: errors,
  });
}

export async function draftReportSection(
  document: ReportDocumentV2,
  _sectionId: string,
  analysis: ReportSectionAnalysis,
  target: 'report' | 'slides'
) {
  const requestScope = captureSupportScope();
  const token = await (window as any).Clerk?.session?.getToken();
  if (!token) throw new ReportApiError('Sign in to draft with AI.', 401);
  const response = await fetch(`${base}/ai/generate-section-description`, {
    method: 'POST',
    headers: internalApiHeaders(
      `${base}/ai/generate-section-description`,
      { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      false,
      requestScope
    ),
    body: JSON.stringify({
      companyId: document.companyId,
      companyName: document.sourceSnapshot.company.name,
      ...analysis,
      target,
    }),
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.success)
    throw new ReportApiError(
      result?.error || 'AI drafting is unavailable. Your content is unchanged.',
      response.status
    );
  return result.description as string;
}
